import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { uuid } from '@/interfaces';
import { useEffect, useState } from 'react';
import type { ChatMember, PartyKind } from '@shyne/shared/api';
import { ChatRepository } from '@/repositories/chat.repository.ts';

interface Props { 
    open: boolean; 
    onOpenChange: (o: boolean) => void; 
    memberCount: number;
    roomId: uuid;
}

const KIND_LABEL: Partial<Record<PartyKind, string>> = { customer: 'PMC', vendor: 'Vendor', subcontractor: 'Subcontractor' };

const KIND_BADGE: Partial<Record<PartyKind, "default" | "secondary" | "destructive" | "outline">> = { customer: 'default', vendor: 'secondary', subcontractor: 'outline' };

const labelOf = (member: ChatMember) => member.roomRole === 'worker' ? 'Individual' : (member.organization ? KIND_LABEL[member.organization.kind] : undefined) ?? 'Organization';

const badgeOf = (member: ChatMember) => member.roomRole === 'worker' ? 'destructive' : (member.organization ? KIND_BADGE[member.organization.kind] : undefined) ?? 'outline';

export default function MembersSidebar({ open, onOpenChange, memberCount, roomId }: Props) {
    const [members, setMembers] = useState<ChatMember[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;

        setLoading(true);
        ChatRepository.members(roomId)
            .then(setMembers)
            .catch((error) => console.error('Failed to fetch member details:', error))
            .finally(() => setLoading(false));
    }, [open, roomId]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-80">
                <SheetHeader>
                    <SheetTitle>Chat Members ({memberCount})</SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                    {loading && (
                        <p className="text-sm text-muted-foreground">Loading members...</p>
                    )}
                    
                    {!loading && members.map(member => (
                        <div key={member.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-accent">
                            <Avatar className="h-10 w-10">
                                <AvatarImage src={member.picture ?? undefined} />
                                <AvatarFallback>
                                    {(member.name ?? '??').slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                    {member.name ?? 'Unknown'}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                    <Badge 
                                        variant={badgeOf(member)}
                                        className="text-xs"
                                    >
                                        {labelOf(member)}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground truncate">
                                        {member.organization?.name ?? 'Unknown'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {!loading && members.length === 0 && (
                        <p className="text-sm text-muted-foreground">No members found.</p>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}